
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useCreateRole, useUpdateRole, useRolePermissions, useUpdateRolePermissions, CustomRole } from '@/hooks/useRoles';
import { useToast } from '@/hooks/use-toast';
import { useEnhancedPermissions } from '@/hooks/useEnhancedPermissions';

interface RoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: CustomRole;
}

export function RoleDialog({ open, onOpenChange, role }: RoleDialogProps) {
  const { toast } = useToast();
  const { getAllAvailablePermissions } = useEnhancedPermissions();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6366f1'
  });
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const updatePermissions = useUpdateRolePermissions();
  const { data: existingPermissions } = useRolePermissions(role?.id);

  const availablePermissions = getAllAvailablePermissions();

  // تحديث بيانات النموذج عند تغيير الدور
  useEffect(() => {
    if (role && open) {
      setFormData({
        name: role.name,
        description: role.description || '',
        color: role.color
      });
    } else if (!role && open) {
      setFormData({
        name: '',
        description: '',
        color: '#6366f1'
      });
      setSelectedPermissions(new Set());
    }
  }, [role, open]);

  // تحديث الصلاحيات المحددة عند تحميل البيانات
  useEffect(() => {
    if (existingPermissions && role) {
      const enabled = new Set<string>();
      existingPermissions.forEach(perm => {
        if (perm.permission_value) {
          enabled.add(perm.permission_name);
        }
      });
      setSelectedPermissions(enabled);
    }
  }, [existingPermissions, role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading) return;
    
    setLoading(true);
    
    try {
      let roleData;
      if (role) {
        roleData = await updateRole.mutateAsync({ id: role.id, ...formData });
      } else {
        roleData = await createRole.mutateAsync(formData);
      }

      // تحديث الصلاحيات
      const permissionsArray = availablePermissions.map(permName => ({
        permission_name: permName,
        permission_value: selectedPermissions.has(permName)
      }));

      await updatePermissions.mutateAsync({
        roleId: roleData.id,
        permissions: permissionsArray
      });

      toast({
        title: "تم الحفظ",
        description: role ? "تم تحديث الدور بنجاح" : "تم إنشاء الدور بنجاح",
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error saving role:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء الحفظ",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionName: string) => {
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionName)) {
        newSet.delete(permissionName);
      } else {
        newSet.add(permissionName);
      }
      return newSet;
    });
  };

  const toggleAllPermissions = (enabled: boolean) => {
    if (enabled) {
      setSelectedPermissions(new Set(availablePermissions));
    } else {
      setSelectedPermissions(new Set());
    }
  };

  // تجميع الصلاحيات حسب الأقسام باستخدام النظام المحدث
  const groupPermissionsByCategory = () => {
    const { getPermissionsByCategory } = useEnhancedPermissions();
    const categorizedPermissions = getPermissionsByCategory();
    
    const grouped: { [key: string]: string[] } = {};
    
    // أسماء الأقسام بالعربية
    const categoryNames = {
      dashboard: 'الصفحة الرئيسية',
      clients: 'إدارة العملاء',
      tourism: 'البرامج السياحية',
      tourism_management: 'إدارة البرامج السياحية',
      hr: 'الموارد البشرية',
      suppliers: 'الموردون',
      visas: 'التأشيرات',
      tools: 'أدوات العمل',
      admin: 'إدارة النظام',
      analytics: 'التحليلات',
      tasks: 'المهام',
      finance: 'المالية',
      other: 'أخرى'
    };
    
    // تجميع الصلاحيات حسب الأقسام
    Object.entries(categorizedPermissions).forEach(([categoryKey, permissions]) => {
      const categoryName = categoryNames[categoryKey as keyof typeof categoryNames] || categoryKey;
      grouped[categoryName] = permissions;
    });
    
    // إضافة أي صلاحيات غير مصنفة
    const allCategorizedPermissions = Object.values(categorizedPermissions).flat();
    const uncategorizedPermissions = availablePermissions.filter(
      perm => !allCategorizedPermissions.includes(perm)
    );
    
    if (uncategorizedPermissions.length > 0) {
      grouped['صلاحيات أخرى'] = uncategorizedPermissions;
    }

    return grouped;
  };

  const groupedPermissions = groupPermissionsByCategory();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{role ? 'تعديل الدور' : 'إنشاء دور جديد'}</DialogTitle>
          <DialogDescription>
            قم بتعيين اسم الدور ووصفه والصلاحيات المرتبطة به
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">اسم الدور</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="أدخل اسم الدور"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="color">لون الدور</Label>
              <Input
                id="color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="h-10 w-full"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">الوصف</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="أدخل وصف الدور"
              rows={3}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">الصلاحيات</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAllPermissions(true)}
                  disabled={loading}
                >
                  تحديد الكل
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAllPermissions(false)}
                  disabled={loading}
                >
                  إلغاء تحديد الكل
                </Button>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto p-4 border rounded-lg bg-gray-50">
              {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
                <div key={category} className="mb-6">
                  <h3 className="font-semibold text-lg mb-3 text-blue-800 border-b pb-2">
                    {category}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {categoryPermissions.map((permission) => (
                      <div key={permission} className="flex items-center space-x-2 space-x-reverse p-2 hover:bg-white rounded border">
                        <Checkbox
                          id={`permission-${permission}`}
                          checked={selectedPermissions.has(permission)}
                          onCheckedChange={() => handlePermissionToggle(permission)}
                          disabled={loading}
                        />
                        <Label
                          htmlFor={`permission-${permission}`}
                          className="text-sm font-medium leading-none cursor-pointer flex-1"
                        >
                          {permission}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-600">الصلاحيات المفعلة ({selectedPermissions.size}):</span>
              {Array.from(selectedPermissions).map((name) => (
                <Badge key={name} variant="secondary" className="text-xs">
                  {name}
                </Badge>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              إلغاء
            </Button>
            <Button 
              type="submit"
              disabled={loading}
            >
              {loading ? 'جاري الحفظ...' : (role ? 'تحديث' : 'إنشاء')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
